// Per-click signal access for the live pulsed figures. Clicks found by the engine carry, in the bundle, the segment
// the engine cuts around them (band-passed, figures.click_card) and a raw snippet (figures.group_waveforms). Clicks
// the reviewer adds are cut from the piece's full-resolution signals (loaded on demand), the same way.
import { decodeSegment, dftAny, fftrans, hilbert, idftAny, wignerVille, contourMetrics } from './engineCard';
import { amax } from './safeMath';

const b64i16 = (b64, scale) => {
  const bin = atob(b64); const n = bin.length / 2; const out = new Float64Array(n); const s = scale / 32767;
  for (let k = 0; k < n; k += 1) { let v = bin.charCodeAt(2 * k) | (bin.charCodeAt(2 * k + 1) << 8); if (v > 32767) v -= 65536; out[k] = v * s; }
  return out;
};
export function seg(y, p, half) {
  const out = new Float64Array(2 * half + 1);
  for (let k = -half; k <= half; k += 1) { const j = p + k; if (j >= 0 && j < y.length) out[k + half] = y[j]; }
  return out;
}

/** loads the piece's band-passed and raw signals (int16 files next to the bundle) */
export async function loadSignals(piece, resolve = (p) => p) {
  const base = piece.signalsBase || '';
  const urls = piece.signalUrls || {};
  const get = async (name, scale) => {
    const u = urls[name] || resolve(base + name);
    const r = await fetch(u); if (!r.ok) throw new Error(`signal ${r.status}`);
    const buf = await r.arrayBuffer(); const v = new Int16Array(buf); const out = new Float32Array(v.length); const s = scale / 32767;
    for (let i = 0; i < v.length; i += 1) out[i] = v[i] * s; return out;
  };
  const [y, x] = await Promise.all([get(piece.signals.bandpassed, piece.signals.scaleBandpassed), get(piece.signals.raw, piece.signals.scaleRaw)]);
  return { y, x, fs: piece.sampleRate };
}

/** the peak of a click near time t (envelope maximum of the band-passed signal within +-win s) */
export function snapPeak(sig, t, win) {
  const fs = sig.fs; const c = Math.round(t * fs); const w = Math.max(3, Math.round(win * fs));
  let best = c; let bv = -1;
  for (let i = Math.max(0, c - w); i <= Math.min(sig.y.length - 1, c + w); i += 1) { const v = Math.abs(sig.y[i]); if (v > bv) { bv = v; best = i; } }
  return best / fs;
}

export function makeAccess(piece, sig) {
  if (!piece || !piece.card) return null;
  const card = piece.card; const fs = card.fs; const cache = new Map();
  const cardSeg = (i) => { if (!cache.has(i)) cache.set(i, decodeSegment(card, i)); return cache.get(i); };
  const rawCache = new Map();
  // decoder bundles (bundle_decoder.json, 9 Oct 2026) carry no card segments / raw snippets: cut every click from
  // the piece's full-resolution signals instead (loaded on demand, as for clicks the reviewer adds)
  const hasSeg = Array.isArray(card.q) && card.q.length > 0;
  const hasRaw = Array.isArray(piece.raw?.q) && piece.raw.q.length > 0;
  return {
    fs,
    /** band-passed segment of 2*half+1 samples centred on the click (engine _seg on d.y) */
    bp(k, half) {
      if (hasSeg && k.i !== null && k.i !== undefined) return seg(cardSeg(k.i), card.centre[k.i], half);
      if (!sig) return null; return seg(sig.y, Math.round(k.t * fs), half);
    },
    /** raw (mean removed) segment of +-2h samples, h = max(8, int(0.3 ms * fs)) (figures.group_waveforms) */
    raw(k) {
      const h = piece.raw.h;
      if (hasRaw && k.i !== null && k.i !== undefined) { if (!rawCache.has(k.i)) rawCache.set(k.i, b64i16(piece.raw.q[k.i], piece.raw.scale[k.i])); return rawCache.get(k.i); }
      if (!sig) return null; return seg(sig.x, Math.round(k.t * fs), 2 * h);
    },
  };
}

// WVD measures of a click the engine did not measure (added by the reviewer): same maths as the card
export function measureAdded(piece, acc, k) {
  const s = piece.card.settings; const fs = piece.card.fs;
  const hw = Math.max(8, Math.trunc(s.figure_half_window_ms * 1e-3 * fs));
  const sg = acc.bp(k, hw); if (!sg) return null;
  const { W, times, freqs } = wignerVille(sg, fs, s.wvd_lag_window_ms * 1e-3, s.wvd_time_smooth_ms * 1e-3);
  const cm = contourMetrics(W, times, freqs, s.wvd_contour_level_db);
  const half = Math.max(4, Math.trunc(s.click_half_window_ms * 1e-3 * fs));
  const sp = acc.bp(k, half); const nfft = Math.max(s.nfft, 2 ** Math.ceil(Math.log2(sp.length)));
  const { pxx, f } = fftrans(sp, nfft, fs); let kp = 0; pxx.forEach((v, j) => { if (v > pxx[kp]) kp = j; });
  let sw = 0; let swf = 0; pxx.forEach((v, j) => { sw += v * v; swf += v * v * f[j]; });
  const z = hilbert(sp); let pp = 0; let mx = -Infinity; let mn = Infinity; sp.forEach((v) => { if (v > mx) mx = v; if (v < mn) mn = v; }); pp = mx - mn;
  return { wvdLen: cm.length, wvdF0: cm.ridge[0], wvdF1: cm.ridge[cm.ridge.length - 1], wvdSweep: cm.sweep, wvdWidth: cm.width, wvdUpper: cm.upperMax,
    peak: f[kp], centre: sw > 0 ? swf / sw : null, pp: 20 * Math.log10(pp + 1e-12), peakdb: 20 * Math.log10(amax(Array.from(z.re, (v, j) => Math.hypot(v, z.im[j]))) + 1e-12) };
}

// scipy.signal.resample (FFT method) for any length
export function resample(x, m) {
  const n = x.length; const Xr = Float64Array.from(x); const Xi = new Float64Array(n);
  dftAny(Xr, Xi);
  const Yr = new Float64Array(m); const Yi = new Float64Array(m); const nh = Math.floor((n + 1) / 2);
  for (let k = 0; k < nh; k += 1) { Yr[k] = Xr[k]; Yi[k] = Xi[k]; }
  for (let k = 1; k < nh; k += 1) { Yr[m - k] = Xr[n - k]; Yi[m - k] = Xi[n - k]; }
  if (n % 2 === 0) { Yr[n / 2] = Xr[n / 2] / 2; Yi[n / 2] = Xi[n / 2] / 2; Yr[m - n / 2] = Xr[n / 2] / 2; Yi[m - n / 2] = Xi[n / 2] / 2; }
  idftAny(Yr, Yi);
  const out = new Float64Array(m); for (let t = 0; t < m; t += 1) out[t] = (Yr[t] * m) / n;
  return out;
}

// scipy.signal.find_peaks(m, prominence=p): indices of local maxima with at least that prominence
export function findPeaks(m, prom) {
  const out = [];
  for (let i = 1; i < m.length - 1; i += 1) {
    if (!(m[i] > m[i - 1] && m[i] >= m[i + 1])) continue;
    let l = m[i]; for (let j = i - 1; j >= 0 && m[j] <= m[i]; j -= 1) l = Math.min(l, m[j]);
    let r = m[i]; for (let j = i + 1; j < m.length && m[j] <= m[i]; j += 1) r = Math.min(r, m[j]);
    if (m[i] - Math.max(l, r) >= prom) out.push(i);
  }
  return out;
}

export { fftrans, hilbert, wignerVille };
